import css from './RegNumInput.module.css';
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import imgFalgar from '../../assets/cat-falgar.avif';
import imgKomplett from '../../assets/cat-sommarhjul.avif';
import imgDack from '../../assets/cat-sommardack.avif';

/* Plate-search refinement step. After a plate resolves we don't yet
 * know whether the buyer wants kompletta hjul, däck or fälgar — these
 * three product types use the same vehicle fitment data but live as
 * different `pub_adType` values on listings. The picker appends that
 * filter (or skips it for "Visa allt") before the redirect to /annonser. */
const REG_PRODUCT_OPTIONS = [
  { id: 'komplett', adType: 'wheel', name: 'Kompletta hjul', desc: 'Fälg + däck monterade', image: imgKomplett },
  { id: 'dack', adType: 'tire', name: 'Däck', desc: 'Sommar, vinter, m.m.', image: imgDack },
  { id: 'falgar', adType: 'rim', name: 'Fälgar', desc: 'Fälgar utan däck', image: imgFalgar },
];

const SearchOutlinedIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </svg>
);

import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { SET_LICENSE_PLATE_INFO } from '../../ducks/currentLicensePlate.duck';

/* localStorage key for the last-looked-up regnr. Persists across
 * page reloads / sessions so features like Jämför on the listing
 * page can auto-populate without asking the user to re-enter. Cleared
 * when the user logs out (handled in user.duck.js logout flow) or
 * clears the reg input explicitly. */
const REG_STORAGE_KEY = 'wheelplace.regNumber';
const SERIES_STORAGE_KEY = 'wheelplace.regSeries';
const storeRegNumber = reg => {
  if (typeof window === 'undefined' || !reg) return;
  try {
    window.localStorage.setItem(REG_STORAGE_KEY, reg);
  } catch {
    /* quota / disabled storage — non-fatal */
  }
};
const storeRegSeries = series => {
  if (typeof window === 'undefined') return;
  try {
    if (series) window.localStorage.setItem(SERIES_STORAGE_KEY, series);
    else window.localStorage.removeItem(SERIES_STORAGE_KEY);
  } catch {
    /* non-fatal */
  }
};
import { useHistory } from 'react-router-dom';
import { getLicensePlateInfo, updateUserProfileGeneral } from '../../util/api';
import { getPrimarySetup } from '../../util/carSetups';
import { carFitment, carSearchParams, withSeasonDefault } from '../../util/carSearchParams';
import { recordPlateSearch } from '../../util/searchKeywordTracker';
import { rememberCar } from '../../util/savedCar';
import { Modal } from '../../components'
import { useConfiguration } from '../../context/configurationContext';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';


export function RegNumInput(props) {
  const { savedRegNum, changeRegNum } = props;
  /* Hub-bore options come from the hosted listing config — the "navhål"
   * filter is every configured value at or above the car's own hub. */
  const config = useConfiguration();
  const hubHoleOptions =
    (config?.listing?.listingFields || []).find(f => f.key === 'hubHole')?.enumOptions || [];
  const [licensePlateInfo, setLicensePlateInfo] = useState(null);
  const [cache, setCache] = useState({}); // Step 1: State for caching results
  const dispatch = useDispatch();
  const userId = useSelector(state => state.user.currentUser?.id?.uuid);
  const history = useHistory();
  const isFirstRender = useRef(true);
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [seriesModalOpen, setSeriesModalOpen] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [pendingQuery, setPendingQuery] = useState('');
  const [modelOptions, setModelOptions] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [carName, setCarName] = useState(null);
  /* Product-picker step shown after plate resolves on search flows
   * (page='searchPage'). pendingSearch holds the resolved /annonser
   * URL parts; the picker appends pub_adType then pushes history. */
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [pendingSearch, setPendingSearch] = useState(null);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (licensePlateInfo?.result || licensePlateInfo?.data && licensePlateInfo?.data[0]) {
      if (props.page === 'searchPage') {
        handleRedirect();
      } else {
        props.updateState(licensePlateInfo);
      }
    }

  }, [licensePlateInfo, props.page]);

  useEffect(() => {
    if (selectedModel) {
      const query = savedRegNum.replace(/[\s-]/g, '');
      const fetchData = async () => {
        const data = await getLicensePlateInfo({ licensePlate: query, selectedModel: selectedModel });
        // console.log('data', data)
        if (data.status === 'choose_series') {
          setSeriesOptions(data?.availableSeries)
          setPendingQuery({
            licensePlate: query,
            make: data.make,
            model: selectedModel,
            year: data.year
          });
        }

      };
      fetchData();
    }
  }, [selectedModel]);


  function extractTireMeasurements(str) {
    const tireWidth = str.slice(0, 3);
    const profile = str.match(/\/([^R]*)R/)[1];
    const diameter = str.match(/R([^.]*)\./)[1];
    return { tireWidth, profile, diameter };
  }

  const filterEmptyEntries = valuesArray => valuesArray.filter(value => value !== '');

  function extractValues(tires, prop) {
    const values = tires.map(tire => (tire ? tire[prop] : ''));

    const newSet = [...new Set([...values])];
    const newValues = filterEmptyEntries(newSet);

    return newValues.join('%2C');
  }

  function extractBothValues(front, rear, prop) {
    const frontValues = front.map(tire => (tire ? tire[prop] : ''));
    const rearValues = rear.map(tire => (tire ? tire[prop] : ''));

    const newSet = [...new Set([...frontValues, ...rearValues])];
    const newValues = filterEmptyEntries(newSet);

    return newValues.join('%2C');
  }

  const handleSubmit = async e => {
    e.preventDefault();

    const format = /^[a-zA-Z]{3}\d{2}(\d|[a-zA-Z])$/;

    const query = savedRegNum.replace(/[\s-]/g, '');
    if (!format.test(query)) {
      console.error('Invalid format. The input must be in the format "abc123" or "abc12a".');
      return;
    }

    if (cache[query]) {
      console.log('Retrieving data from cache');
      setLicensePlateInfo(cache[query]);
      dispatch({
        type: SET_LICENSE_PLATE_INFO,
        payload: cache[query],
        reg: query,
      });
      storeRegNumber(query);
      // P25009-482 BR-4 — every successful lookup joins the recent-cars list.
      rememberCar({ reg: query, data: cache[query] });
      /* The search page's "Byt bil" panel (P25009-491) runs the car's search
       * from updateState. The same plate typed again sets the same cached
       * object, which does not re-run the effect below — so say it here.
       * The listing wizard too: "Inte rätt bil? Ändra" keeps the plate
       * (P25009-502), and searching it again must bring the car back. */
      if ((props.page === 'carSwitch' || props.page === 'newListingPage') && props.updateState) {
        props.updateState(cache[query]);
      }
      if (userId) {
        updateUserProfileGeneral({
          params: { id: userId, publicData: { regNumber: query } },
        }).catch(() => {});
      }
    } else {
      // const encodedQuery = encodeURIComponent(query);
      // const api = process.env.REACT_APP_API_URL;
      // const userIdParam = userId ? `?userId=${encodeURIComponent(userId)}` : '';
      // const apiUrl = `${api}/api/proxy/wheelplace/${encodedQuery}${userIdParam}`;

      try {
        // const response = await fetch(apiUrl);
        // if (!response.ok) {
        //   throw new Error(`HTTP error! status: ${response.status}`);
        // }
        // const data = await response.json();

        const data = await getLicensePlateInfo({ licensePlate: query });

        if (data.status === 'choose_series') {
          console.log('data', data)
          setSeriesOptions(data.availableSeries);
          setPendingQuery({
            licensePlate: query,
            make: data.make,
            model: selectedModel || data.model,
            year: data.year
          });
          setCarName(data?.car_name);
           if(props.page==="editListingPage" && props.setCarName){
            props.setCarName(data.car_name)
          }
          /* Auto-select when the resolved car's production year falls
           * inside exactly one variant's [yearFrom, yearTo] range. The
           * backend supplies cleaned numeric bounds on each option, so
           * we just compare integers — no re-parsing of the label. If
           * zero or multiple variants match, leave the dropdown empty
           * and force a manual choice (ENG-AC-08, ENG-AC-10). AC-11
           * (only after plate lookup) is satisfied by construction: we
           * are inside the plate-lookup response handler. */
          const carYearMatch = String(data.year || '').match(/\d{4}/);
          const carYear = carYearMatch ? Number(carYearMatch[0]) : null;
          let autoSelectedSlug = null;
          if (carYear) {
            const yearMatches = (data.availableSeries || []).filter(opt =>
              opt.yearFrom != null &&
              opt.yearTo != null &&
              carYear >= opt.yearFrom &&
              carYear <= opt.yearTo
            );
            if (yearMatches.length === 1) {
              autoSelectedSlug = yearMatches[0].slug;
            }
          }
          setSelectedSeries(autoSelectedSlug);
          setSeriesModalOpen(true);
          return;
        }
        if (data.status === 'choose_Modal') {
          setModelOptions(data.availableModals);
          setSeriesOptions([]);
          setSelectedSeries(null);
          setSeriesModalOpen(true);

        }

        console.log('data', data);

        // Step 3: Update cache with the new data
        setCache({
          ...cache,
          [query]: data,
        });
        // Dispatch action here
        dispatch({
          type: SET_LICENSE_PLATE_INFO,
          payload: data,
          reg: query,
        });
        setLicensePlateInfo(data);
        storeRegNumber(query);
        storeRegSeries(null);
        rememberCar({ reg: query, data });
        if (userId) {
          updateUserProfileGeneral({
            params: { id: userId, publicData: { regNumber: query, regSeries: null } },
          }).catch(() => {});
        }
        recordPlateSearch({ rawInput: query, data });
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    }
  };

  const extractOriginalTires = tire => {
    const tireReplaced = tire.replace(/[a-zA-Z/\s]/g, '');
    const tireWidth = tireReplaced.slice(0, 3);
    const profile = tireReplaced.slice(3, 5);
    const diameter = tireReplaced.slice(5, 7);

    return { tireWidth, profile, diameter };
  };



  const handleRedirect = () => {
    // const frontWheels = [];
    // const rearWheels = [];

    // licensePlateInfo?.result?.tire_info?.tires.map(set => {
    //   frontWheels.push(set.front);
    //   rearWheels.push(set.rear);
    // });

    // const originalFrontTire = extractOriginalTires(
    //   licensePlateInfo.result.original_tires.front_tires
    // );
    // const originalRearTire = extractOriginalTires(
    //   licensePlateInfo.result.original_tires.rear_tires
    // );

    // const frontTires = frontWheels?.map(wheel => {
    //   return extractTireMeasurements(wheel.tire);
    // });

    // const rearTires = rearWheels?.map(wheel => {
    //   return extractTireMeasurements(wheel.tire);
    // });

    // const allFrontTires = [originalFrontTire, ...frontTires];
    // const allRearTires = [originalRearTire, ...rearTires];

    // let filterParams = {};

    // if (props.listingType === 'tire') {
    //   filterParams = {
    //     profileFront: !extractValues(allFrontTires, 'profile')
    //       ? ''
    //       : `pub_profileFront=${extractValues(allFrontTires, 'profile')}`,
    //     profileRear: !extractValues(allRearTires, 'profile')
    //       ? ''
    //       : `pub_profileRear=${extractValues(allRearTires, 'profile')}`,
    //     tireWidthFront: !extractValues(allFrontTires, 'tireWidth')
    //       ? ''
    //       : `pub_tireWidthFront=${extractValues(allFrontTires, 'tireWidth')}`,
    //     tireWidthRear: !extractValues(allRearTires, 'tireWidth')
    //       ? ''
    //       : `pub_tireWidthRear=${extractValues(allRearTires, 'tireWidth')}`,
    //     diameter: !extractBothValues(allFrontTires, allRearTires, 'diameter')
    //       ? ''
    //       : `pub_diameter=${extractBothValues(allFrontTires, allRearTires, 'diameter')}`,
    //   };
    //   listingTypeKey = 1;
    // } else if (props.listingType === 'rim') {
    //   filterParams = {
    //     diameter: !extractBothValues(allFrontTires, allRearTires, 'diameter')
    //       ? ''
    //       : `pub_diameter=${extractBothValues(allFrontTires, allRearTires, 'diameter')}`,
    //     numberOfBolts: !licensePlateInfo.result.tire_info.bolts
    //       ? ''
    //       : `pub_numberOfBolts=${licensePlateInfo.result.tire_info.bolts}`,
    //     boltCircle: !licensePlateInfo.result.tire_info.bolt_circle
    //       ? ''
    //       : `pub_boltCircle=${licensePlateInfo.result.tire_info.bolt_circle}`,
    //     hubHole: !licensePlateInfo.result.tire_info.center_bore
    //       ? ''
    //       : `pub_hubHole=${licensePlateInfo.result.tire_info.center_bore}`,
    //   };
    //   listingTypeKey = 2;
    // } else {
    //   filterParams = {
    //     profileFront: !extractValues(allFrontTires, 'profile')
    //       ? ''
    //       : `pub_profileFront=${extractValues(allFrontTires, 'profile')}`,
    //     profileRear: !extractValues(allRearTires, 'profile')
    //       ? ''
    //       : `pub_profileRear=${extractValues(allRearTires, 'profile')}`,
    //     tireWidthFront: !extractValues(allFrontTires, 'tireWidth')
    //       ? ''
    //       : `pub_tireWidthFront=${extractValues(allFrontTires, 'tireWidth')}`,
    //     tireWidthRear: !extractValues(allRearTires, 'tireWidth')
    //       ? ''
    //       : `pub_tireWidthRear=${extractValues(allRearTires, 'tireWidth')}`,
    //     diameter: !extractBothValues(allFrontTires, allRearTires, 'diameter')
    //       ? ''
    //       : `pub_diameter=${extractBothValues(allFrontTires, allRearTires, 'diameter')}`,
    //     numberOfBolts: !licensePlateInfo.result.tire_info.bolts
    //       ? ''
    //       : `pub_numberOfBolts=${licensePlateInfo.result.tire_info.bolts}`,
    //     boltCircle: !licensePlateInfo.result.tire_info.bolt_circle
    //       ? ''
    //       : `pub_boltCircle=${licensePlateInfo.result.tire_info.bolt_circle}`,
    //     hubHole: !licensePlateInfo.result.tire_info.center_bore
    //       ? ''
    //       : `pub_hubHole=${licensePlateInfo.result.tire_info.center_bore}`,
    //   };

    //  const frontRims =  licensePlateInfo?.result?.original_tires?.front_rims;
    //  let et = frontRims.includes('ET') ? frontRims.split("ET")[1] : frontRims.includes('x') ? frontRims.split("x")[2] : null
    //  et ||= licensePlateInfo?.data?.car?.wheels?.[0]?.front?.rim_offset?.toString(10);
    // let encodedEtRange = "";
    // if (et) {
    //   const parsedEt = Math.round(parseFloat(et));
    //   const etRange = `${parsedEt - 10},${parsedEt}`;
    //    encodedEtRange = encodeURIComponent(etRange);
    // }


    // const selectedHubHole = licensePlateInfo.result.tire_info.center_bore;
    // const result = getHubHolesAbove(selectedHubHole);
    // const encodedResult = encodeURIComponent(result.join(','));
    // console.log(encodedResult);



    // const filterParams = {
    //   // pub_hubHole: result ? `pub_hubHole=${encodedResult}`:undefined,
    //   // pub_etFront: encodedEtRange ? `pub_etFront=${encodedEtRange}` : undefined,
    //   boltCircle: !licensePlateInfo.result.tire_info.center_bore
    //     ? ''
    //     : `pub_boltCircle=${licensePlateInfo.result.tire_info.bolt_circle}`,
    //   carBrandCategory: !licensePlateInfo.result.brand
    //     ? ''
    //     : `pub_categoryLevel1=${licensePlateInfo.result.brand.toLowerCase()}`,
    //   carModelCategory: !licensePlateInfo.result.series
    //     ? ''
    //     : `pub_categoryLevel2=${licensePlateInfo.result.series.toLowerCase()}`,
    //   numberOfBolts: !licensePlateInfo.result.tire_info.bolts
    //     ? ''
    //     : `pub_numberOfBolts=${licensePlateInfo.result.tire_info.bolts}`,
    // };

    let car = null;

    // Check which data structure is available (either data[0] or result)
    if (licensePlateInfo.data && licensePlateInfo.data.length > 0) {
      car = licensePlateInfo.data[0]; // Use the first item from data
    } else if (licensePlateInfo.result) {
      car = licensePlateInfo.result; // Use result structure
    } else {
      console.error('No valid car data found');
      // Handle error or exit early
      return;
    }

    const brand = car.brand ? car.brand : car?.make?.name;
    const model = car?.series?.name ? car.series.name : car?.series ? car.series : car?.model?.name;

    /* Staggered car (different front and rear sizes)? The set flow
     * (P25009-364) is still offered from the car panel, but it is no longer
     * applied here: a set is one specific pair of sizes, and forcing it on the
     * landing search is exactly the hidden condition that made the plate
     * search return nothing. The buyer picks it deliberately, or narrows by
     * size, from <CarInfoSidebar />. */
    const primarySetup = getPrimarySetup(licensePlateInfo);

    /* Defer the redirect. Open the product-picker so the buyer can
     * narrow to hjul / däck / fälgar (or "Visa allt") — the chosen tab
     * decides which fitment filters apply (handlePickProduct). */
    setPendingSearch({
      /* Without the space the input shows ("ABC 123"), so the URL carries the
       * same plate string as localStorage and the saved-car record. */
      regNumber: typeof savedRegNum === 'string' ? savedRegNum.replace(/[\s-]/g, '') : savedRegNum,
      data: licensePlateInfo,
      setup: primarySetup,
      staggered: !!primarySetup?.isStaggered,
      carName: [brand, model].filter(Boolean).join(' '),
    });
    setProductPickerOpen(true);

    let listingTypeKey = 0;

    if (props.listingType === 'tire') {
      listingTypeKey = 1;
    } else if (props.listingType === 'rim') {
      listingTypeKey = 2;
    }

    dispatch({
      type: 'SET_LISTINGTYPE',
      payload: listingTypeKey,
    });
  };

  /* Picker selection handler. `adType` is null for "Visa allt" (no
   * product filter), otherwise 'wheel' | 'tire' | 'rim' which maps to the
   * pub_adType field on listings.
   *
   * The chosen tab decides the filters, all built by the one shared
   * builder so the three tabs cannot drift apart again (BR-4). No size has
   * been picked yet, so this is the car's fitment only: bolt pattern,
   * offset window and hub bore for hjul/fälg, nothing at all for däck. The
   * car itself rides along as carMake/carModel, which rank the results
   * rather than filter them (BR-2). */
  const handlePickProduct = adType => {
    if (!pendingSearch) return;
    /* "Visa allt" spans all three product types, and a tyre carries no bolt
     * pattern or hub bore — so it gets the car context only, never fitment
     * filters that a tyre listing could not satisfy. */
    const fitment = carFitment(pendingSearch.data);
    const params = adType
      ? withSeasonDefault(carSearchParams({ data: pendingSearch.data, adType, hubHoleOptions }))
      : {
          ...(fitment.make ? { carMake: fitment.make } : {}),
          ...(fitment.model ? { carModel: fitment.model } : {}),
        };
    const search = new URLSearchParams(params);
    if (pendingSearch.regNumber) search.set('regNumber', pendingSearch.regNumber);
    setProductPickerOpen(false);
    setPendingSearch(null);
    history.push({ pathname: '/annonser', search: `?${search.toString()}` });
  };

  // "compact" variant is used in the global header (40px tall vs 64px hero).
  // Classes are composed so default usage is unchanged.
  const rootClass =
    props.size === 'compact'
      ? `${css.searchBoxReg} ${css.searchBoxRegCompact}`
      : props.size === 'inline'
        ? `${css.searchBoxReg} ${css.searchBoxRegInline}`
        : css.searchBoxReg;

  return (
    <>
      <div className={rootClass} data-reg-input="true">
        <input
          ref={props.inputRef || undefined}
          type="text"
          placeholder="ABC 123"
          value={savedRegNum}
          onChange={event => {
            const raw = event.target.value.toUpperCase().replace(/\s/g, '');
            const formatted =
              raw.length > 3 ? `${raw.slice(0, 3)} ${raw.slice(3, 6)}` : raw;
            changeRegNum(formatted);
          }}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleSubmit(event);
            }
          }}
          disabled={props.disabled}
        />

        <button
          type="button"
          onClick={handleSubmit}
          disabled={props.disabled}
          className={props.submitLabel ? css.regSubmitButtonText : css.regSubmitButton}
          aria-label={props.submitLabel || 'Sök'}
        >
          {/* A text button ("Kolla") where the input asks a question rather
            * than starts a search — the listing page's fit check. */}
          {props.submitLabel || <SearchOutlinedIcon />}
        </button>
        {seriesModalOpen && (
          <Modal
            id='ModalWithPortal'
            usePortal={true}
            isOpen={seriesModalOpen}
            onClose={() => {
              setSeriesModalOpen(false);
            }}
            onManageDisableScrolling={(componentId, disableScrolling) =>
              dispatch(manageDisableScrolling(componentId, disableScrolling))}>
            <div className={css.modalContent}>
              {modelOptions && modelOptions.length > 0 &&
                <>
                  <h3 className={css.inputLabel}>Välj Modell</h3>
                  <select onChange={e => setSelectedModel(e.target.value)} defaultValue="">
                    <option value="" disabled>Välj en</option>
                    {modelOptions.map(option => (
                      <option key={option.slug} value={option.slug}>
                        {option.name_en}
                      </option>
                    ))}
                  </select>
                </>
              }

              {seriesOptions && seriesOptions.length > 0 &&
                <>
                  <h3 className={css.inputLabel}>Välj motor {carName ? `(${carName})` :` (${pendingQuery?.make} ${pendingQuery?.model}  ${pendingQuery.year})`}</h3>
                  <select
                    onChange={e => setSelectedSeries(e.target.value)}
                    value={selectedSeries || ''}
                  >
                    <option value="" disabled>Välj en</option>
                    {seriesOptions.map(option => (
                      <option key={option.slug} value={option.slug}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </>
              }


              <button
                onClick={async () => {
                  if (!selectedSeries) return;
                  const data = await getLicensePlateInfo({
                    ...pendingQuery,
                    selectedModel,
                    selectedSeries
                  });
                  setLicensePlateInfo(data);
                  const reg = pendingQuery?.licensePlate || savedRegNum?.replace(/[\s-]/g, '');
                  dispatch({
                    type: SET_LICENSE_PLATE_INFO,
                    payload: data,
                    reg,
                  });
                  storeRegNumber(reg);
                  storeRegSeries(selectedSeries);
                  rememberCar({ reg, series: selectedSeries, data });
                  if (userId) {
                    updateUserProfileGeneral({
                      params: {
                        id: userId,
                        publicData: { regNumber: reg, regSeries: selectedSeries },
                      },
                    }).catch(() => {});
                  }
                  /* Carry the user-visible motor label from the
                   * dropdown so the dashboard can show "1.5 BlueHDi
                   * 100 Manual" alongside the year — two plates
                   * can resolve to the same trim/year on paper but
                   * be different motors. */
                  const selectedSeriesOption = seriesOptions.find(o => o.slug === selectedSeries);
                  const seriesName = selectedSeriesOption?.name || '';
                  recordPlateSearch({ rawInput: reg, data, seriesName });
                  setSeriesModalOpen(false);
                }}
                className={`${css.confirmButton} ${!selectedSeries ? css.disabledButton : ''}`}
                disabled={!selectedSeries}
              >
                Bekräfta
              </button>
            </div>
          </Modal>
        )}

        {productPickerOpen && pendingSearch && typeof document !== 'undefined' &&
          /* Portal direct to document.body so the overlay escapes
           * the .searchBoxReg `overflow: hidden` ancestor (iOS Safari
           * traps fixed-position descendants under that combination)
           * and any sibling Modal portal at z-index 100. */
          ReactDOM.createPortal(
          <div
            className={css.productPickerOverlay}
            role="dialog"
            aria-modal="true"
            aria-label="Välj produkttyp"
          >
            <div className={css.productPickerSheet}>
              <button
                type="button"
                onClick={() => {
                  setProductPickerOpen(false);
                  setPendingSearch(null);
                }}
                className={css.productPickerClose}
                aria-label="Stäng"
              >
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 6l12 12M6 18L18 6" />
                </svg>
              </button>
              <h3 className={css.productPickerHeading}>
                {pendingSearch.carName
                  ? `Vad letar du efter till din ${pendingSearch.carName}?`
                  : 'Vad letar du efter?'}
              </h3>
              <p className={css.productPickerHint}>
                {pendingSearch.staggered
                  ? 'Din bil har olika dimensioner fram och bak. Välj kategori — sedan kan du välja en komplett sats i bilrutan till vänster.'
                  : 'Välj kategori — vi visar annonser som passar din bil.'}
              </p>
              <div
                className={css.productPickerGrid}
                role="radiogroup"
                aria-label="Produkttyp"
              >
                {REG_PRODUCT_OPTIONS.map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    role="radio"
                    aria-checked="false"
                    onClick={() => handlePickProduct(opt.adType)}
                    className={css.productPickerCard}
                  >
                    <div className={css.productPickerImageWrap}>
                      <img
                        src={opt.image}
                        alt=""
                        className={css.productPickerImage}
                        loading="lazy"
                      />
                    </div>
                    <span className={css.productPickerName}>{opt.name}</span>
                    <span className={css.productPickerDesc}>{opt.desc}</span>
                  </button>
                ))}
              </div>
              {/* "Visa allt" is offered for every car now. It used to be
                * hidden for a staggered one because the landing search forced
                * a complete set on it; it no longer does, so all three tabs
                * and the skip behave alike (BR-4). */}
              <button
                type="button"
                onClick={() => handlePickProduct(null)}
                className={css.productPickerSkip}
              >
                Visa allt
              </button>
            </div>
          </div>,
          document.body
        )}


      </div>
    </>
  );
}
